import React, { useState, useEffect, useMemo } from "react";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  Info,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  User
} from 'lucide-react';
import Balancete from "./Balancete";
import ClosingChecklist from "./Balancete/components/ClosingChecklist";
import { Card, Col, Row, Space, Typography, Skeleton } from "antd";

const { Title, Paragraph } = Typography;

export function Contabilidade() {

  const company = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("COMPANY")) || {};
    } catch {
      return {};
    }
  }, []);

  const [checklistData, setChecklistData] = useState({ situacaoList: [], loading: false });

  const handleBalanceteData = (data) => {
    setChecklistData(data);
  };


  const theme = {
    primary: '#4f46e5',
    primaryHover: '#4338ca',
    background: '#f8fafc',
    white: '#ffffff',
    border: '#e2e8f0',
    textMain: '#0f172a',
    textSecondary: '#64748b',
    textLight: '#94a3b8',
    emerald: '#059669',
    emeraldBg: '#ecfdf5',
    rose: '#e11d48',
    roseBg: '#fff1f2',
    amber: '#d97706',
  };


  const InfoItem = ({ label, value, subtext, icon: Icon, colorClass, trend }) => (
    <div style={{
      backgroundColor: theme.white,
      padding: '16px',
      borderRadius: '12px',
      border: `1px solid ${theme.border}`,
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      marginBottom: '16px',
      transition: 'box-shadow 0.2s'
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{
          padding: '8px',
          borderRadius: '8px',
          backgroundColor: colorClass.bg,
          color: colorClass.text
        }}>
          <Icon size={20} />
        </div>
        {trend && (
          <span style={{
            fontSize: '12px',
            fontWeight: 'bold',
            padding: '4px 8px',
            borderRadius: '9999px',
            backgroundColor: trend > 0 ? theme.emeraldBg : theme.roseBg,
            color: trend > 0 ? theme.emerald : theme.rose
          }}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div style={{ marginTop: '12px' }}>
        <p style={{
          color: theme.textSecondary,
          fontSize: '12px',
          fontWeight: '500',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          margin: 0
        }}>{label}</p>
        <h3 style={{
          fontSize: '20px',
          fontWeight: 'bold',
          color: theme.textMain,
          marginTop: '4px',
          margin: 0
        }}>{value}</h3>
        {subtext && <p style={{ color: theme.textLight, fontSize: '10px', marginTop: '4px', fontStyle: 'italic', margin: 0 }}>{subtext}</p>}
      </div>
    </div>
  );

  /**
   * Componente Principal da Barra Lateral
   */
  const SidebarInformacoes = ({ loading }) => {
    if (loading) {
      return (
        <div style={{
          backgroundColor: theme.white,
          borderRadius: '12px',
          border: `1px solid ${theme.border}`,
          padding: '20px',
          width: '520px',
          flexShrink: 0
        }}>
          <Skeleton active avatar paragraph={{ rows: 4 }} />
        </div>
      );
    }
    return (
      <div style={{
        backgroundColor: theme.white,
        borderRadius: '12px',
        border: `1px solid ${theme.border}`,
        padding: '20px',
        width: '520px',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', borderBottom: `1px solid ${theme.border}`, paddingBottom: '12px' }}>
          <div style={{ backgroundColor: theme.primary, padding: '6px', borderRadius: '6px', color: theme.white, display: 'flex' }}>
            <Info size={18} />
          </div>
          <div>
            <h2 style={{ fontSize: '14px', fontWeight: 'bold', color: theme.textMain, margin: 0 }}>Dados consolidados do cliente</h2>
          </div>
        </div>

        <div
          style={{
            backgroundColor: "#f8fafc",
            borderRadius: "12px",
            border: `1px solid ${theme.border}`,
            padding: "16px",
            display: "flex",
            gap: "16px"
          }}
        >
          {/* Ícone */}
          <div
            style={{
              height: "40px",
              width: "40px",
              borderRadius: "50%",
              backgroundColor: theme.white,
              border: `1px solid ${theme.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.textLight
            }}
          >
            <User size={18} />
          </div>

          {/* Informações */}
          <div style={{ flex: 1 }}>
            <p
              style={{
                fontSize: "14px",
                fontWeight: 600,
                color: theme.textMain,
                marginBottom: "8px"
              }}
            >
              {company?.nome || "Empresa não informada"}
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "6px 16px",
                fontSize: "12px"
              }}
            >
              <span><strong>CNPJ:</strong> {company?.cnpj || "-"}</span>
              <span><strong>Tipo:</strong> {company?.tipoEmpresa?.id || "-"}</span>

              <span><strong>Folha:</strong> {renderBooleanBadge(company?.folha)}</span>
              <span><strong>Pró-labore:</strong> {renderBooleanBadge(company?.prolabore)}</span>

              <span><strong>Regime:</strong> {company?.regimeTributario?.id || "-"}</span>
              <span><strong>Número de Funcionários:</strong> {company?.numeroFuncionarios || "-"}</span>

              <span style={{ gridColumn: "1 / span 2" }}>
                <strong>Nível Contábil:</strong>{" "}
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: "6px",
                    backgroundColor: `${nivel.color}15`,
                    color: nivel.color,
                    fontWeight: 500
                  }}
                >
                  {nivel.label}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBooleanBadge = (value) => {
    if (value === true)
      return <span style={{ color: "#16a34a", fontWeight: 500 }}>Sim</span>;

    if (value === false)
      return <span style={{ color: "#dc2626", fontWeight: 500 }}>Não</span>;

    return "-";
  };

  const nivelContabilMap = {
    C: { label: "Caixa", color: "#16a34a" },
    P: { label: "Planilha", color: "#2563eb" },
    B: { label: "Banco", color: "#9333ea" }
  };

  const nivel = nivelContabilMap[company?.nivelContabil] || {
    label: "Não informado",
    color: theme.textSecondary
  };

  return (
    <div style={{
      backgroundColor: '#f1f5f9',
      padding: '40px',
      fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
    }}>
      <div style={{ maxWidth: '1920px', margin: '0 auto' }}>
        <Row gutter={[24, 24]} align="top">
          <Col span={24}>
            {/* Cabeçalho da Página */}
            <Title level={2} style={{ margin: '0 0 4px 0', fontWeight: 700, fontSize: '24px' }}>
              Lançamentos Contábeis
            </Title>
            <Paragraph type="secondary" style={{ fontSize: '14px', marginBottom: '24px' }}>
              Consulte e edite os lançamentos contábeis do cliente.
            </Paragraph>

            {/* Painel de Informações e Banner de Automação Lado a Lado */}
            <div style={{
              display: 'flex',
              gap: '24px',
              alignItems: 'stretch',
              marginBottom: '24px'
            }}>
              <SidebarInformacoes loading={checklistData.loading} />
              <div style={{ flex: 1 }}>
                <ClosingChecklist 
                  situacaoList={checklistData.situacaoList} 
                  loading={checklistData.loading} 
                />
              </div>
            </div>

            {/* Balancete de Verificação */}
            <Balancete onDataChange={handleBalanceteData} />
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default Contabilidade;